import { useState, useRef, useEffect } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';
import { ManageCategoriesModal } from '../components/ManageCategoriesModal';

/**
 * Media asset type definition matching the API response.
 */
interface MediaAsset {
  id: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  size: number;
  type: string;
  path: string;
  url: string;
  alt: string | null;
  description: string | null;
  tags: string[] | null;
  categoryId: string | null;
  uploader: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
  usageCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Media category type definition matching the bot configuration.
 */
interface MediaCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  order: number;
}

/**
 * AI Analysis data structure for the AI Analysis section.
 */
interface AIAnalysis {
  description: string;
  confidence: number;
  suggestedTags: string[];
}

/**
 * Media Library page component with split-pane design.
 *
 * Provides a visual interface for managing uploaded media assets.
 * Features a grid view on the left and a detail panel on the right
 * with AI Analysis capabilities.
 */
export function MediaPage() {
  const { currentBotId } = useProject();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'filename' | 'size' | 'usageCount'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null);
  const [aiAnalysis, setAIAnalysis] = useState<AIAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const limit = 20;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state for editing metadata in the detail panel
  const [editAlt, setEditAlt] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTagsInput, setEditTagsInput] = useState('');
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);

  // Fetch bot data to get media categories
  const botQuery = trpc.bots.get.useQuery(
    { id: currentBotId! },
    { enabled: !!currentBotId }
  );
  const mediaCategories: MediaCategory[] = (botQuery.data?.mediaCategories as MediaCategory[] | null | undefined) ?? [];

  // AI analysis query for cached results
  const analysisQuery = trpc.media.getAnalysis.useQuery(
    { id: selectedAsset?.id ?? '' },
    {
      enabled: !!selectedAsset?.id,
    }
  );

  // Fetch media assets
  const mediaQuery = trpc.media.list.useQuery({
    limit,
    offset,
    type: typeFilter as 'image' | 'audio' | 'video' | 'document' | undefined || undefined,
    categoryId: categoryFilter || undefined,
    search: searchQuery || undefined,
    sortBy,
    sortOrder,
    botId: currentBotId ?? undefined,
  });

  // Send statistics query for the selected asset
  const sendStatsQuery = trpc.media.getSendStats.useQuery(
    { id: selectedAsset?.id ?? '' },
    {
      enabled: !!selectedAsset?.id,
    }
  );

  // Batch send statistics for all assets in the list (for consistent display)
  const assetIds = mediaQuery.data?.assets.map((a) => a.id) ?? [];
  const batchSendStatsQuery = trpc.media.getSendStatsBatch.useQuery(
    { assetIds },
    {
      enabled: assetIds.length > 0,
    }
  );

  // AI analysis mutation for running/refreshing analysis
  const analyzeMutation = trpc.media.analyze.useMutation({
    onSuccess: (data) => {
      setAIAnalysis(data);
      setAnalysisError(null);
      // Invalidate the query to update the cache
      utils.media.getAnalysis.invalidate({ id: selectedAsset?.id ?? '' });
    },
    onError: (error) => {
      setAnalysisError(error.message);
    },
  });

  // Initialize form values when asset changes
  useEffect(() => {
    if (selectedAsset) {
      setEditAlt(selectedAsset.alt || '');
      setEditDescription(selectedAsset.description || '');
      setEditTagsInput(selectedAsset.tags?.join(', ') || '');
      setEditCategoryId(selectedAsset.categoryId);
      setHasChanges(false);
      setSaveError(null);
      setAnalysisError(null);
      // Reset AI analysis state - will be populated by query
      setAIAnalysis(null);
    } else {
      setAIAnalysis(null);
      setAnalysisError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset]);

  // Handle analysis query result and errors
  useEffect(() => {
    if (analysisQuery.data) {
      setAIAnalysis(analysisQuery.data);
      setAnalysisError(null);
    } else if (analysisQuery.error) {
      setAnalysisError(analysisQuery.error.message);
    }
  }, [analysisQuery.data, analysisQuery.error]);

  /**
   * Handles triggering AI analysis (for initial analysis or refresh)
   */
  const handleRunAnalysis = (force: boolean = false) => {
    if (!selectedAsset) return;
    setAnalysisError(null);
    analyzeMutation.mutate({ id: selectedAsset.id, force });
  };

  // Track changes
  useEffect(() => {
    if (!selectedAsset) return;
    const currentAlt = selectedAsset.alt || '';
    const currentDescription = selectedAsset.description || '';
    const currentTags = selectedAsset.tags?.join(', ') || '';
    const currentCategoryId = selectedAsset.categoryId;

    const changed =
      editAlt !== currentAlt ||
      editDescription !== currentDescription ||
      editTagsInput !== currentTags ||
      editCategoryId !== currentCategoryId;

    setHasChanges(changed);
  }, [editAlt, editDescription, editTagsInput, editCategoryId, selectedAsset]);

  // Fetch stats for the header
  const statsQuery = trpc.media.stats.useQuery({
    botId: currentBotId ?? undefined,
  });

  const utils = trpc.useUtils();

  // Upload mutation
  const uploadMutation = trpc.media.upload.useMutation({
    onSuccess: () => {
      utils.media.list.invalidate();
      utils.media.stats.invalidate();
      setUploadError(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error) => {
      setUploadError(error.message);
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  // Delete mutation
  const deleteMutation = trpc.media.delete.useMutation({
    onSuccess: () => {
      utils.media.list.invalidate();
      utils.media.stats.invalidate();
      // Clear selection if deleted asset was selected
      if (selectedAsset && deleteMutation.variables?.id === selectedAsset.id) {
        setSelectedAsset(null);
      }
    },
  });

  // Update mutation for editing metadata
  const updateMutation = trpc.media.update.useMutation({
    onSuccess: (updatedAsset) => {
      // Update the selected asset with new data
      setSelectedAsset(updatedAsset);
      // Invalidate the list to reflect changes in the grid
      utils.media.list.invalidate();
      setHasChanges(false);
    },
    onError: (error) => {
      setSaveError(error.message);
    },
  });

  /**
   * Handles file selection and upload.
   */
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await uploadFile(file);
  };

  /**
   * Reads a file and returns its content as a Base64 string.
   */
  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handles file upload from a File object.
   */
  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      const base64Data = await readFileAsBase64(file);
      await uploadMutation.mutateAsync({
        filename: file.name,
        mimeType: file.type,
        base64Data,
        botId: currentBotId ?? undefined,
      });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
      setIsUploading(false);
    }
  };

  /**
   * Handles drag over event on the dropzone.
   */
  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploading) {
      setIsDragOver(true);
    }
  };

  /**
   * Handles drag leave event on the dropzone.
   */
  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  /**
   * Handles file drop on the dropzone.
   */
  const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (isUploading) return;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      await uploadFile(file);
    }
  };

  /**
   * Handles asset deletion with confirmation.
   */
  const handleDelete = (assetId: string, filename: string) => {
    if (confirm(`Are you sure you want to delete "${filename}"?`)) {
      deleteMutation.mutate({ id: assetId });
    }
  };

  /**
   * Handles clicking on an asset to view/edit its details.
   */
  const handleAssetClick = (asset: MediaAsset) => {
    setSelectedAsset(asset);
  };

  /**
   * Handles closing the detail panel.
   */
  const handleCloseDetail = () => {
    setSelectedAsset(null);
  };

  /**
   * Handles saving metadata changes.
   */
  const handleSaveMetadata = async () => {
    if (!selectedAsset || !hasChanges || updateMutation.isPending) return;

    setSaveError(null);

    // Parse tags from comma-separated string
    const tags = editTagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    await updateMutation.mutateAsync({
      id: selectedAsset.id,
      alt: editAlt || undefined,
      description: editDescription || undefined,
      tags: tags.length > 0 ? tags : undefined,
      categoryId: editCategoryId,
    });
  };

  /**
   * Handles adding a new tag.
   */
  const handleAddTag = () => {
    if (!newTagInput.trim()) return;
    const currentTags = editTagsInput ? editTagsInput.split(',').map(t => t.trim()).filter(t => t) : [];
    if (!currentTags.includes(newTagInput.trim())) {
      const newTags = [...currentTags, newTagInput.trim()];
      setEditTagsInput(newTags.join(', '));
    }
    setNewTagInput('');
  };

  /**
   * Handles removing a tag.
   */
  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = editTagsInput.split(',').map(t => t.trim()).filter(t => t && t !== tagToRemove);
    setEditTagsInput(currentTags.join(', '));
  };

  /**
   * Gets parsed tags from the input string.
   */
  const getParsedTags = (): string[] => {
    return editTagsInput.split(',').map(t => t.trim()).filter(t => t.length > 0);
  };

  /**
   * Handles applying a suggested tag from AI analysis.
   */
  const handleApplySuggestedTag = (tag: string) => {
    const currentTags = getParsedTags();
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag];
      setEditTagsInput(newTags.join(', '));
    }
  };

  /**
   * Handles copying the AI description to the description field.
   */
  const handleCopyAIDescription = () => {
    if (aiAnalysis?.description) {
      setEditDescription(aiAnalysis.description);
    }
  };

  /**
   * Handles selecting all suggested tags from AI analysis.
   */
  const handleSelectAllSuggestedTags = () => {
    if (!aiAnalysis?.suggestedTags) return;
    const currentTags = getParsedTags();
    const newTags = [...currentTags];
    for (const tag of aiAnalysis.suggestedTags) {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
      }
    }
    setEditTagsInput(newTags.join(', '));
  };

  /**
   * Formats file size for display.
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Formats date for display.
   */
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };

  /**
   * Gets an icon for the media type.
   */
  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'image':
        return '🖼️';
      case 'audio':
        return '🎵';
      case 'video':
        return '🎬';
      case 'document':
        return '📄';
      default:
        return '📁';
    }
  };

  /**
   * Gets confidence badge color based on score.
   */
  const getConfidenceColor = (confidence: number): { bg: string; text: string } => {
    if (confidence >= 90) return { bg: 'rgba(16, 185, 129, 0.15)', text: '#059669' };
    if (confidence >= 75) return { bg: 'rgba(245, 158, 11, 0.15)', text: '#D97706' };
    return { bg: 'rgba(239, 68, 68, 0.15)', text: '#DC2626' };
  };

  /**
   * Gets category name by ID.
   */
  const getCategoryName = (categoryId: string | null): string | null => {
    if (!categoryId) return null;
    const category = mediaCategories.find(c => c.id === categoryId);
    return category?.name ?? null;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Media Library</h1>
        {statsQuery.data && (
          <div style={styles.statsContainer}>
            <span style={styles.statBadge}>
              {statsQuery.data.total} files
            </span>
            <span style={styles.statBadge}>
              {formatFileSize(statsQuery.data.totalSize)}
            </span>
          </div>
        )}
      </div>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        disabled={isUploading}
        style={styles.fileInput}
        id="file-upload"
        accept="image/*,audio/*,video/*,.pdf,.txt,.json"
      />

      {/* Tab-style Filters */}
      <div style={styles.filtersRow}>
        <div style={styles.tabFilters}>
          {[
            { value: '', label: 'All Types' },
            { value: 'image', label: 'Image' },
            { value: 'video', label: 'Video' },
            { value: 'audio', label: 'Audio' },
            { value: 'document', label: 'Document' },
          ].map((type) => (
            <button
              key={type.value}
              onClick={() => {
                setTypeFilter(type.value);
                setOffset(0);
              }}
              style={{
                ...styles.tabButton,
                ...(typeFilter === type.value ? styles.tabButtonActive : {}),
              }}
            >
              {type.label}
            </button>
          ))}
        </div>
        {mediaCategories.length > 0 && (
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setOffset(0);
            }}
            style={styles.categorySelect}
          >
            <option value="">All Categories</option>
            {mediaCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => setIsManageCategoriesOpen(true)}
          style={styles.manageCategoriesButton}
        >
          Manage Categories
        </button>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOffset(0);
          }}
          placeholder="Search files..."
          style={styles.searchInput}
        />
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
            setOffset(0);
          }}
          style={styles.sortSelect}
        >
          <option value="createdAt-desc">Newest First</option>
          <option value="createdAt-asc">Oldest First</option>
          <option value="usageCount-desc">Most Used</option>
          <option value="usageCount-asc">Least Used</option>
          <option value="filename-asc">Name A-Z</option>
          <option value="filename-desc">Name Z-A</option>
          <option value="size-desc">Largest</option>
          <option value="size-asc">Smallest</option>
        </select>
      </div>

      {uploadError && (
        <div style={styles.uploadError}>{uploadError}</div>
      )}

      {/* Main content area with grid and detail panel */}
      <div style={styles.mainContent}>
        {/* Left Panel - Grid */}
        <div style={styles.gridPanel}>
          {/* Upload Dropzone */}
          <label
            htmlFor="file-upload"
            style={{
              ...styles.dropzone,
              ...(isUploading ? styles.dropzoneUploading : {}),
              ...(isDragOver ? styles.dropzoneDragOver : {}),
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div style={{
              ...styles.dropzoneIcon,
              ...(isDragOver ? styles.dropzoneIconDragOver : {}),
            }}>
              {isUploading ? '⏳' : isDragOver ? '📥' : '✨'}
            </div>
            <p style={styles.dropzoneTitle}>
              {isUploading ? 'Uploading...' : isDragOver ? 'Drop to upload' : 'Drop files here or click to upload'}
            </p>
            <p style={styles.dropzoneSubtext}>
              Images, videos, audio, documents
            </p>
          </label>

          {/* Media Grid */}
          {mediaQuery.isLoading ? (
            <div style={styles.loading}>Loading media...</div>
          ) : mediaQuery.error ? (
            <div style={styles.error}>
              Error loading media: {mediaQuery.error.message}
            </div>
          ) : mediaQuery.data?.assets.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📁</div>
              <h3 style={styles.emptyTitle}>No media files found</h3>
              <p style={styles.emptyText}>
                {typeFilter || searchQuery
                  ? 'Try adjusting your filters'
                  : 'Upload your first file to get started'}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.grid}>
                {mediaQuery.data?.assets.map((asset) => (
                  <div
                    key={asset.id}
                    style={{
                      ...styles.card,
                      ...(selectedAsset?.id === asset.id ? styles.cardSelected : {}),
                    }}
                    onClick={() => handleAssetClick(asset)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAssetClick(asset);
                      }
                    }}
                  >
                    {/* Thumbnail Preview */}
                    <div style={styles.preview}>
                      {asset.type === 'image' ? (
                        <img
                          src={asset.url}
                          alt={asset.alt || asset.originalFilename}
                          style={styles.previewImage}
                        />
                      ) : (
                        <span style={styles.previewIcon}>
                          {getTypeIcon(asset.type)}
                        </span>
                      )}
                      {/* Type Badge */}
                      <span style={styles.typeBadgeOverlay}>
                        {asset.type.toUpperCase()}
                      </span>
                    </div>

                    {/* Info */}
                    <div style={styles.cardInfo}>
                      <p style={styles.cardFilename} title={asset.originalFilename}>
                        {asset.originalFilename.length > 25
                          ? asset.originalFilename.substring(0, 22) + '...'
                          : asset.originalFilename}
                      </p>

                      {/* Tags preview */}
                      {asset.tags && asset.tags.length > 0 && (
                        <div style={styles.tagsPreview}>
                          {asset.tags.slice(0, 3).map((tag) => (
                            <span key={tag} style={styles.tagBadge}>
                              {tag}
                            </span>
                          ))}
                          {asset.tags.length > 3 && (
                            <span style={styles.tagMore}>
                              +{asset.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Category badge */}
                      {asset.categoryId && getCategoryName(asset.categoryId) && (
                        <div style={styles.categoryPreview}>
                          <span style={styles.categoryBadge}>
                            {getCategoryName(asset.categoryId)}
                          </span>
                        </div>
                      )}

                      <div style={styles.cardMeta}>
                        <span style={styles.usageText}>
                          {(() => {
                            const sendCount = batchSendStatsQuery.data?.[asset.id]?.total ?? 0;
                            return sendCount === 0 ? (
                              <span style={{ color: '#9ca3af' }}>Not used</span>
                            ) : (
                              <span style={{ fontWeight: 500 }}>
                                {sendCount} {sendCount === 1 ? 'send' : 'sends'}
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {mediaQuery.data && mediaQuery.data.pagination.total > limit && (
                <div style={styles.pagination}>
                  <button
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    disabled={offset === 0}
                    style={styles.pageButton}
                  >
                    Previous
                  </button>
                  <span style={styles.pageInfo}>
                    {offset + 1} - {Math.min(offset + limit, mediaQuery.data.pagination.total)} of {mediaQuery.data.pagination.total}
                  </span>
                  <button
                    onClick={() => setOffset((o) => o + limit)}
                    disabled={!mediaQuery.data.pagination.hasMore}
                    style={styles.pageButton}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Panel - Detail */}
        <div style={styles.detailPanel}>
          {!selectedAsset ? (
            <div style={styles.noSelection}>
              <div style={styles.noSelectionIcon}>🖼️</div>
              <h3 style={styles.noSelectionTitle}>No media selected</h3>
              <p style={styles.noSelectionText}>
                Select a media item from the grid to view details and AI analysis
              </p>
            </div>
          ) : (
            <div style={styles.detailContent}>
              {/* Detail Header */}
              <div style={styles.detailHeader}>
                <div style={styles.detailHeaderLeft}>
                  <h2 style={styles.detailTitle}>
                    {selectedAsset.originalFilename.length > 30
                      ? selectedAsset.originalFilename.substring(0, 27) + '...'
                      : selectedAsset.originalFilename}
                  </h2>
                  <span style={styles.detailType}>{selectedAsset.type.toUpperCase()}</span>
                </div>
                <button
                  onClick={handleCloseDetail}
                  style={styles.closeButton}
                  aria-label="Close detail panel"
                >
                  ×
                </button>
              </div>

              {/* Scrollable Content */}
              <div style={styles.detailScrollable}>
                {/* Preview Section */}
                <div style={styles.detailPreview}>
                  {selectedAsset.type === 'image' ? (
                    <img
                      src={selectedAsset.url}
                      alt={selectedAsset.alt || selectedAsset.originalFilename}
                      style={styles.detailPreviewImage}
                    />
                  ) : (
                    <div style={styles.detailPreviewPlaceholder}>
                      <span style={styles.detailPreviewIcon}>
                        {getTypeIcon(selectedAsset.type)}
                      </span>
                      <span style={styles.detailPreviewLabel}>
                        {selectedAsset.type.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                <a
                  href={selectedAsset.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.viewOriginalLink}
                >
                  View Original ↗
                </a>

                {/* AI Analysis Section */}
                <div style={styles.aiSection}>
                  <div style={styles.aiSectionHeader}>
                    <h3 style={styles.sectionTitle}>AI Analysis</h3>
                    <div style={styles.aiHeaderActions}>
                      <span style={styles.aiBadge}>AI</span>
                      {aiAnalysis && !analyzeMutation.isPending && (
                        <button
                          onClick={() => handleRunAnalysis(true)}
                          style={styles.refreshButton}
                          title="Re-analyze with AI"
                        >
                          Refresh
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Loading State */}
                  {(analyzeMutation.isPending || analysisQuery.isLoading) && !aiAnalysis && (
                    <div style={styles.aiLoadingCard}>
                      <span style={styles.aiLoadingIcon}>&#x1F50D;</span>
                      <p style={styles.aiLoadingText}>
                        {analyzeMutation.isPending ? 'Analyzing media with AI...' : 'Loading cached analysis...'}
                      </p>
                    </div>
                  )}

                  {/* Error State */}
                  {analysisError && !aiAnalysis && !analyzeMutation.isPending && (
                    <div style={styles.aiErrorCard}>
                      <p style={styles.aiErrorText}>{analysisError}</p>
                    </div>
                  )}

                  {/* No Analysis Yet - Show Analyze Button */}
                  {!aiAnalysis && !analyzeMutation.isPending && !analysisQuery.isLoading && !analysisError && (
                    <div style={styles.noAnalysisCard}>
                      <p style={styles.noAnalysisText}>This media hasn't been analyzed yet.</p>
                      <button
                        onClick={() => handleRunAnalysis(false)}
                        style={styles.analyzeButton}
                      >
                        Analyze with AI
                      </button>
                    </div>
                  )}

                  {/* AI Description Card */}
                  {aiAnalysis && (
                    <>
                      <div style={styles.aiDescriptionCard}>
                        <div style={styles.aiDescriptionHeader}>
                          <span style={styles.aiDescriptionLabel}>AI Description</span>
                          <span
                            style={{
                              ...styles.confidenceBadge,
                              backgroundColor: getConfidenceColor(aiAnalysis.confidence).bg,
                              color: getConfidenceColor(aiAnalysis.confidence).text,
                            }}
                          >
                            {aiAnalysis.confidence}% confident
                          </span>
                        </div>
                        <p style={styles.aiDescriptionText}>
                          {aiAnalysis.description}
                        </p>
                        <button
                          onClick={handleCopyAIDescription}
                          style={styles.copyDescriptionButton}
                          title="Copy AI description to the description field"
                        >
                          Copy to Description
                        </button>
                      </div>

                      {/* Suggested Tags */}
                      <div style={styles.suggestedTagsSection}>
                        <div style={styles.suggestedTagsHeader}>
                          <span style={styles.suggestedTagsLabel}>Suggested Tags</span>
                          <button
                            onClick={handleSelectAllSuggestedTags}
                            style={styles.selectAllTagsButton}
                            title="Add all suggested tags"
                          >
                            Select All
                          </button>
                        </div>
                        <div style={styles.suggestedTagsContainer}>
                          {aiAnalysis.suggestedTags.map((tag) => (
                            <button
                              key={tag}
                              onClick={() => handleApplySuggestedTag(tag)}
                              style={{
                                ...styles.suggestedTag,
                                ...(getParsedTags().includes(tag) ? styles.suggestedTagApplied : {}),
                              }}
                              disabled={getParsedTags().includes(tag)}
                            >
                              {getParsedTags().includes(tag) ? '✓' : '+'} {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* File Information */}
                <div style={styles.infoSection}>
                  <h3 style={styles.sectionTitle}>File Information</h3>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Filename</span>
                      <span style={styles.infoValue} title={selectedAsset.originalFilename}>
                        {selectedAsset.originalFilename}
                      </span>
                    </div>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>MIME Type</span>
                      <span style={styles.infoValue}>{selectedAsset.mimeType}</span>
                    </div>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Size</span>
                      <span style={styles.infoValue}>{formatFileSize(selectedAsset.size)}</span>
                    </div>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Uploaded</span>
                      <span style={styles.infoValue}>{formatDate(selectedAsset.createdAt)}</span>
                    </div>
                    {selectedAsset.uploader && (
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Uploaded by</span>
                        <span style={styles.infoValue}>
                          {selectedAsset.uploader.name || selectedAsset.uploader.email || 'Unknown'}
                        </span>
                      </div>
                    )}
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Usage Count</span>
                      <span style={styles.infoValue}>
                        {selectedAsset.usageCount === 0 ? (
                          <span style={{ color: '#9ca3af' }}>Not used yet</span>
                        ) : (
                          <span style={{ fontWeight: 500 }}>
                            {selectedAsset.usageCount} {selectedAsset.usageCount === 1 ? 'time' : 'times'}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Send Statistics Section */}
                <div style={styles.infoSection}>
                  <h3 style={styles.sectionTitle}>Send Statistics</h3>
                  {sendStatsQuery.isLoading ? (
                    <div style={{ fontSize: '13px', color: '#9B9285' }}>Loading stats...</div>
                  ) : sendStatsQuery.data ? (
                    <div style={styles.sendStatsGrid}>
                      <div style={styles.sendStatItem}>
                        <span style={styles.sendStatValue}>{sendStatsQuery.data.last24Hours}</span>
                        <span style={styles.sendStatLabel}>Last 24h</span>
                      </div>
                      <div style={styles.sendStatItem}>
                        <span style={styles.sendStatValue}>{sendStatsQuery.data.last7Days}</span>
                        <span style={styles.sendStatLabel}>Last 7 days</span>
                      </div>
                      <div style={styles.sendStatItem}>
                        <span style={styles.sendStatValue}>{sendStatsQuery.data.total}</span>
                        <span style={styles.sendStatLabel}>Total</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#9B9285' }}>No send data available</div>
                  )}
                </div>

                {/* Tags Section */}
                <div style={styles.tagsSection}>
                  <h3 style={styles.sectionTitle}>Tags</h3>
                  <div style={styles.tagsChipContainer}>
                    {getParsedTags().map((tag) => (
                      <span key={tag} style={styles.tagChip}>
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          style={styles.tagRemoveButton}
                          aria-label={`Remove ${tag} tag`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {getParsedTags().length === 0 && (
                      <span style={styles.noTagsText}>No tags yet</span>
                    )}
                  </div>
                  <div style={styles.addTagRow}>
                    <input
                      type="text"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Add a tag..."
                      style={styles.addTagInput}
                    />
                    <button
                      onClick={handleAddTag}
                      disabled={!newTagInput.trim()}
                      style={{
                        ...styles.addTagButton,
                        ...(!newTagInput.trim() ? styles.addTagButtonDisabled : {}),
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Category Section */}
                {mediaCategories.length > 0 && (
                  <div style={styles.categorySection}>
                    <h3 style={styles.sectionTitle}>Category</h3>
                    <select
                      value={editCategoryId || ''}
                      onChange={(e) => setEditCategoryId(e.target.value || null)}
                      style={styles.categorySelectDetail}
                    >
                      <option value="">No Category</option>
                      {mediaCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Metadata Section */}
                <div style={styles.metadataSection}>
                  <h3 style={styles.sectionTitle}>Metadata</h3>

                  {saveError && (
                    <div style={styles.errorMessage}>{saveError}</div>
                  )}

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel} htmlFor="alt-text">
                      Alt Text
                    </label>
                    <input
                      id="alt-text"
                      type="text"
                      value={editAlt}
                      onChange={(e) => setEditAlt(e.target.value)}
                      placeholder="Descriptive text for accessibility"
                      style={styles.formInput}
                      maxLength={500}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel} htmlFor="description">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Detailed description of the media content"
                      style={styles.formTextarea}
                      rows={3}
                      maxLength={2000}
                    />
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div style={styles.detailFooter}>
                <button
                  onClick={() => handleDelete(selectedAsset.id, selectedAsset.originalFilename)}
                  disabled={deleteMutation.isPending}
                  style={styles.deleteButton}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={handleSaveMetadata}
                  style={{
                    ...styles.saveButton,
                    ...((!hasChanges || updateMutation.isPending) ? styles.saveButtonDisabled : {}),
                  }}
                  disabled={!hasChanges || updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Manage Categories Modal */}
      <ManageCategoriesModal
        isOpen={isManageCategoriesOpen}
        onClose={() => setIsManageCategoriesOpen(false)}
        onSave={() => {
          // Refetch bot data to update the categories list
          botQuery.refetch();
        }}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1400px',
    height: 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
    background: '#FAFAF8',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '400',
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  statsContainer: {
    display: 'flex',
    gap: '8px',
  },
  statBadge: {
    padding: '6px 12px',
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
  },
  fileInput: {
    display: 'none',
  },
  uploadError: {
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '10px',
    fontSize: '14px',
  },
  // Tab-style filters
  filtersRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tabFilters: {
    display: 'flex',
    gap: '8px',
  },
  tabButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    background: 'white',
    color: '#5C564E',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  tabButtonActive: {
    border: 'none',
    background: '#3D3A36',
    color: 'white',
  },
  searchInput: {
    padding: '8px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '12px',
    backgroundColor: '#ffffff',
    outline: 'none',
    width: '200px',
    fontFamily: 'inherit',
  },
  categorySelect: {
    padding: '8px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '12px',
    backgroundColor: '#ffffff',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235C564E' d='M2.5 4.5l3.5 3.5 3.5-3.5'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: '32px',
  },
  sortSelect: {
    padding: '8px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '12px',
    backgroundColor: '#ffffff',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235C564E' d='M2.5 4.5l3.5 3.5 3.5-3.5'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: '32px',
  },
  manageCategoriesButton: {
    padding: '8px 14px',
    backgroundColor: '#f5f3ff',
    border: '1px solid #c4b5fd',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#7C3AED',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  // Main content area - split pane layout
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '20px',
    flex: 1,
    minHeight: 0,
  },
  // Left panel - Grid
  gridPanel: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'auto',
  },
  // Right panel - Detail
  detailPanel: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // Upload Dropzone
  dropzone: {
    border: '2px dashed rgba(93, 86, 78, 0.2)',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    marginBottom: '16px',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease',
    display: 'block',
  },
  dropzoneUploading: {
    opacity: 0.7,
    cursor: 'wait',
  },
  dropzoneDragOver: {
    border: '2px dashed #3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1), inset 0 0 20px rgba(59, 130, 246, 0.05)',
    transform: 'scale(1.01)',
  },
  dropzoneIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'rgba(59, 130, 246, 0.1)',
    margin: '0 auto 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    transition: 'transform 0.3s ease, background 0.3s ease',
  },
  dropzoneIconDragOver: {
    transform: 'scale(1.2) translateY(-2px)',
    background: 'rgba(59, 130, 246, 0.2)',
  },
  dropzoneTitle: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#5C564E',
    margin: '0 0 2px',
  },
  dropzoneSubtext: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  loading: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#9B9285',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '10px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '8px',
    margin: '0 0 8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  // Media Grid
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px',
  },
  // Media Card
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  cardSelected: {
    border: '1px solid #3b82f6',
    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
  },
  // Thumbnail Preview
  preview: {
    width: '100%',
    height: '100px',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  previewIcon: {
    fontSize: '28px',
  },
  typeBadgeOverlay: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    fontSize: '9px',
    fontWeight: '600',
    color: '#706A62',
  },
  // Card Info
  cardInfo: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  cardFilename: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '6px',
    margin: '0 0 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Tags preview
  tagsPreview: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '3px',
    marginBottom: '6px',
  },
  tagBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(59, 130, 246, 0.1)',
    fontSize: '9px',
    fontWeight: '500',
    color: '#5C564E',
  },
  tagMore: {
    fontSize: '9px',
    color: '#9B9285',
    padding: '2px',
  },
  // Category preview in card
  categoryPreview: {
    marginBottom: '6px',
  },
  categoryBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(139, 92, 246, 0.1)',
    fontSize: '9px',
    fontWeight: '500',
    color: '#8B5CF6',
  },
  cardMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
  },
  sizeText: {
    fontSize: '10px',
    color: '#9B9285',
  },
  usageText: {
    fontSize: '10px',
    color: '#9B9285',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  pageButton: {
    padding: '6px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  pageInfo: {
    fontSize: '12px',
    color: '#9B9285',
  },
  // No selection state
  noSelection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
  },
  noSelectionIcon: {
    fontSize: '56px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  noSelectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#5C564E',
    margin: '0 0 8px',
  },
  noSelectionText: {
    fontSize: '14px',
    color: '#9B9285',
    textAlign: 'center',
    margin: 0,
  },
  // Detail panel content
  detailContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  detailHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  detailTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailType: {
    padding: '3px 8px',
    borderRadius: '4px',
    background: 'rgba(59, 130, 246, 0.1)',
    fontSize: '10px',
    fontWeight: '600',
    color: '#3b82f6',
    flexShrink: 0,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '22px',
    color: '#9B9285',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  detailScrollable: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
  },
  // Detail Preview
  detailPreview: {
    width: '100%',
    height: '180px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginBottom: '12px',
  },
  detailPreviewImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  detailPreviewPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  detailPreviewIcon: {
    fontSize: '48px',
  },
  detailPreviewLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#9B9285',
    textTransform: 'uppercase',
  },
  viewOriginalLink: {
    display: 'inline-block',
    fontSize: '13px',
    color: '#3b82f6',
    textDecoration: 'none',
    marginBottom: '20px',
  },
  // AI Section
  aiSection: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'rgba(139, 92, 246, 0.04)',
    borderRadius: '10px',
    border: '1px solid rgba(139, 92, 246, 0.1)',
  },
  aiSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px',
  },
  aiHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  aiBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
    fontSize: '10px',
    fontWeight: '600',
    color: 'white',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  // AI Description Card
  aiDescriptionCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '14px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  aiDescriptionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  aiDescriptionLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#5C564E',
  },
  confidenceBadge: {
    padding: '3px 8px',
    borderRadius: '9999px',
    fontSize: '10px',
    fontWeight: '600',
  },
  aiDescriptionText: {
    fontSize: '13px',
    color: '#5C564E',
    lineHeight: '1.5',
    margin: 0,
  },
  // Copy Description Button
  copyDescriptionButton: {
    marginTop: '10px',
    padding: '6px 12px',
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#8B5CF6',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  // Send Statistics Grid
  sendStatsGrid: {
    display: 'flex',
    gap: '16px',
  },
  sendStatItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
  },
  sendStatValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2D3748',
    marginBottom: '4px',
  },
  sendStatLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  // Suggested Tags
  suggestedTagsSection: {
    marginBottom: '14px',
  },
  suggestedTagsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  suggestedTagsLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  selectAllTagsButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '500',
    color: '#8B5CF6',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  suggestedTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  suggestedTag: {
    padding: '5px 10px',
    borderRadius: '6px',
    background: 'white',
    border: '1px dashed rgba(139, 92, 246, 0.3)',
    fontSize: '11px',
    fontWeight: '500',
    color: '#8B5CF6',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  suggestedTagApplied: {
    background: 'rgba(139, 92, 246, 0.1)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    color: '#7C3AED',
    cursor: 'default',
  },
  // AI Loading & Error States
  aiLoadingCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '14px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  aiLoadingIcon: {
    fontSize: '24px',
  },
  aiLoadingText: {
    fontSize: '13px',
    color: '#5C564E',
    margin: 0,
  },
  aiErrorCard: {
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '14px',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  aiErrorText: {
    fontSize: '13px',
    color: '#dc2626',
    margin: 0,
  },
  refreshButton: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#8B5CF6',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  noAnalysisCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '14px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  noAnalysisText: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  analyzeButton: {
    padding: '8px 16px',
    backgroundColor: '#8B5CF6',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  // Info Section
  infoSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '12px',
    margin: '0 0 12px 0',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  infoLabel: {
    fontSize: '11px',
    color: '#9B9285',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: '13px',
    color: '#3D3A36',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Category Section in detail panel
  categorySection: {
    marginBottom: '20px',
  },
  categorySelectDetail: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
  },
  // Tags Section
  tagsSection: {
    marginBottom: '20px',
  },
  tagsChipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    fontSize: '12px',
    fontWeight: '500',
    color: '#3b82f6',
  },
  tagRemoveButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '14px',
    color: '#3b82f6',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    opacity: 0.7,
  },
  noTagsText: {
    fontSize: '12px',
    color: '#9B9285',
    fontStyle: 'italic',
  },
  addTagRow: {
    display: 'flex',
    gap: '8px',
  },
  addTagInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '12px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  addTagButton: {
    padding: '8px 14px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  addTagButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  // Metadata Section
  metadataSection: {
    marginBottom: '0',
  },
  formGroup: {
    marginBottom: '14px',
  },
  formLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: '#5C564E',
    marginBottom: '6px',
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  formTextarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  errorMessage: {
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '12px',
    marginBottom: '14px',
  },
  // Footer
  detailFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '14px 20px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  deleteButton: {
    padding: '10px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
};

export default MediaPage;
